import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import CloseIcon from '@mui/icons-material/Close'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import { useChat } from '../../context/useChat'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { Button } from '../Common'
import styles from './chat.module.css'

interface MemberManagerPanelProps {
  channelId: string
  onClose: () => void
}

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

export const MemberManagerPanel = ({ channelId, onClose }: MemberManagerPanelProps) => {
  const { channelsList, members, addMember, removeMember, leaveChannel } = useChat()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()
  const [busyUid, setBusyUid] = useState<string | null>(null)

  const channel = useMemo(
    () => channelsList.find((c) => c.id === channelId) ?? null,
    [channelsList, channelId],
  )

  const memberUids = useMemo(() => new Set(Object.keys(channel?.roles ?? {})), [channel])
  const isCurrentUserAdmin = currentUser ? channel?.roles[currentUser.uid] === 'admin' : false

  const presentMembers = useMemo(
    () => members.filter((m) => m.uid && memberUids.has(m.uid)),
    [members, memberUids],
  )
  const addableMembers = useMemo(
    () => members.filter((m) => m.uid && !memberUids.has(m.uid)),
    [members, memberUids],
  )

  if (!channel) return null

  const handleRemove = async (uid: string) => {
    setBusyUid(uid)
    try {
      await removeMember(channelId, uid)
      showToast(t('NLSChatToastMemberRemoved'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSChatErrorRemoveMemberFailed'), 'error')
    } finally {
      setBusyUid(null)
    }
  }

  const handleAdd = async (uid: string) => {
    setBusyUid(uid)
    try {
      await addMember(channelId, uid)
      showToast(t('NLSChatToastMemberAdded'), 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSChatErrorAddMemberFailed'), 'error')
    } finally {
      setBusyUid(null)
    }
  }

  const handleLeave = async () => {
    try {
      await leaveChannel(channelId)
      showToast(t('NLSChatToastLeftChannel'), 'success')
      onClose()
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('NLSChatErrorLeaveChannelFailed'), 'error')
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose} role="presentation">
      <motion.div
        className={styles.memberPanel}
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 12, opacity: 0 }}
      >
        <div className={styles.sidebarHeader}>
          <h3 className={styles.modalTitle}>{t('NLSChatLabelMembers')}</h3>
          <Button variant="tertiary" size="sm" icon={<CloseIcon />} onClick={onClose} aria-label={t('NLSChatActionClose')}>
            <span className="sr-only">{t('NLSChatActionClose')}</span>
          </Button>
        </div>

        <div>
          <span className={styles.fieldLabel}>{t('NLSChatLabelInThisChannel', { count: presentMembers.length })}</span>
          <div className={styles.memberPicker} style={{ marginTop: 6 }}>
            {presentMembers.map((member) => (
              <div key={member.uid} className={styles.memberRow}>
                <span className={styles.memberAvatar}>
                  {member.avatarUrl
                    ? <img src={member.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initialsOf(member.name)}
                </span>
                <span style={{ flex: 1 }}>
                  {member.name}
                  {channel.roles[member.uid] === 'admin' && (
                    <span className={styles.channelLock} style={{ marginLeft: 6 }}> · {t('NLSChatLabelAdmin')}</span>
                  )}
                </span>
                {isCurrentUserAdmin && member.uid !== currentUser?.uid && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<PersonRemoveOutlinedIcon />}
                    onClick={() => handleRemove(member.uid)}
                    loading={busyUid === member.uid}
                    aria-label={t('NLSChatActionRemoveMember')}
                    title={t('NLSChatActionRemoveMember')}
                  >
                    <span className="sr-only">{t('NLSChatActionRemove')}</span>
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {isCurrentUserAdmin && addableMembers.length > 0 && (
          <div>
            <span className={styles.fieldLabel}>{t('NLSChatLabelAddMembers')}</span>
            <div className={styles.memberPicker} style={{ marginTop: 6 }}>
              {addableMembers.map((member) => (
                <div key={member.uid} className={styles.memberRow}>
                  <span className={styles.memberAvatar}>
                    {member.avatarUrl
                      ? <img src={member.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : initialsOf(member.name)}
                  </span>
                  <span style={{ flex: 1 }}>{member.name}</span>
                  <Button
                    variant="tertiary"
                    size="sm"
                    icon={<PersonAddOutlinedIcon />}
                    onClick={() => handleAdd(member.uid)}
                    loading={busyUid === member.uid}
                    aria-label={t('NLSChatActionAddMember')}
                    title={t('NLSChatActionAddMember')}
                  >
                    <span className="sr-only">{t('NLSChatActionAdd')}</span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.modalActions} style={{ justifyContent: 'space-between' }}>
          <Button variant="tertiary" icon={<LogoutIcon />} onClick={handleLeave}>
            {t('NLSChatActionLeaveChannel')}
          </Button>
          <Button variant="primary" onClick={onClose}>{t('NLSChatActionDone')}</Button>
        </div>
      </motion.div>
    </div>
  )
}
